import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { projects } from '../data/projects';

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const featuredProjects = projects.slice(0, 6);

  useEffect(() => {
    // Handle hash navigation when component mounts or hash changes
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If no hash, scroll to top
      window.scrollTo(0, 0);
    }
  }, [location.hash, location.pathname]);

  const handleProjectClick = (projectId) => {
    navigate(`/project/${projectId}`);
    window.scrollTo(0, 0);
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden hero-gradient">
        <div className="absolute inset-0 pattern-grid"></div>
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-green-700 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute top-0 left-0 -ml-20 -mt-20 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-white mb-8">
              <span className="flex h-2 w-2 rounded-full bg-green-400 animate-pulse"></span>
              <span className="text-xs font-semibold tracking-wider uppercase">
                Stockholm <span className="mx-2 text-slate-400">✈</span> Riyadh
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight mb-8 leading-tight">
              Swedish Innovation.
              <br />
              <span className="text-gradient font-serif italic">Saudi Ambition.</span>
            </h1>

            <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto leading-relaxed">
              We are your strategic technology partner, bridging Scandinavian digital engineering with the transformative goals of{' '}
              <span className="text-white font-semibold">Vision 2030</span>.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={scrollToContact}
                className="px-8 py-4 bg-white text-slate-900 rounded-full font-bold hover:bg-yellow-400 transition duration-300 shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              >
                Start Your Transformation
              </button>
              <button
                onClick={() => navigate('/portfolio')}
                className="px-8 py-4 bg-transparent border border-white/30 text-white rounded-full font-semibold hover:bg-white/10 transition duration-300"
              >
                View Our Portfolio
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-10 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-semibold text-slate-400 uppercase tracking-widest mb-8">
            Trusted by Global Giants
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
            <div
              className="text-2xl font-bold text-slate-800 flex items-center gap-2 cursor-pointer"
              onClick={() => handleProjectClick('hm')}
            >
              <i className="fab fa-h-square"></i> H&M
            </div>
            <div
              className="text-2xl font-bold text-slate-800 flex items-center gap-2 cursor-pointer"
              onClick={() => handleProjectClick('electrolux')}
            >
              <i className="fas fa-bolt"></i> ELECTROLUX
            </div>
            <div
              className="text-2xl font-bold text-slate-800 flex items-center gap-2 cursor-pointer"
              onClick={() => handleProjectClick('taxi')}
            >
              <i className="fas fa-car"></i> TAXI STOCKHOLM
            </div>
            <div
              className="text-2xl font-bold text-slate-800 flex items-center gap-2 cursor-pointer"
              onClick={() => handleProjectClick('storytel')}
            >
              <i className="fas fa-book-open"></i> STORYTEL
            </div>
            <div
              className="text-2xl font-bold text-slate-800 flex items-center gap-2 cursor-pointer"
              onClick={() => handleProjectClick('certainli')}
            >
              <i className="fas fa-robot"></i> CERTAINLI
            </div>
            <div
              className="text-2xl font-bold text-slate-800 flex items-center gap-2 cursor-pointer"
              onClick={() => handleProjectClick('hpappen')}
            >
              <i className="fas fa-graduation-cap"></i> HP-APPEN
            </div>
            <div
              className="text-2xl font-bold text-slate-800 flex items-center gap-2 cursor-pointer"
              onClick={() => handleProjectClick('moodsocial')}
            >
              <i className="fas fa-users"></i> MOOD SOCIAL
            </div>
          </div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl font-bold text-slate-900">Featured Case Studies</h2>
              <p className="text-slate-500 mt-2">Proven scale and innovation.</p>
            </div>
            <button
              onClick={() => navigate('/portfolio')}
              className="text-green-700 font-semibold hover:text-green-900 transition hidden md:block"
            >
              View All Projects <i className="fas fa-arrow-right ml-2"></i>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProjects.map((project) => (
              <div
                key={project.id}
                onClick={() => handleProjectClick(project.id)}
                className="group cursor-pointer rounded-xl overflow-hidden relative h-64 shadow-lg"
              >
                <img
                  src={project.image}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  alt={project.title}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-white font-bold text-xl">{project.title}</h3>
                  <p className="text-slate-300 text-sm">{project.shortDesc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <button
              onClick={() => navigate('/portfolio')}
              className="text-green-700 font-semibold hover:text-green-900 transition"
            >
              View All Projects <i className="fas fa-arrow-right ml-2"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Vision 2030 Section */}
      <section id="vision2030" className="py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
        <div className="absolute right-0 top-0 w-1/2 h-full bg-gradient-to-l from-green-700/20 to-transparent"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <div className="inline-block px-3 py-1 border border-yellow-400 text-yellow-400 rounded-full text-xs font-bold tracking-widest mb-4">
                VISION 2030 ALIGNED
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 font-serif">Empowering The Kingdom's Future</h2>
              <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                Our mission goes beyond code. We are deeply committed to the social and economic goals of Vision 2030. We believe in knowledge transfer and building local capacity.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-yellow-400 text-xl">
                    <i className="fa-solid fa-venus"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white">Women in Tech</h4>
                    <p className="text-slate-400 text-sm mt-1">
                      We are passionate advocates for gender equality. Our core business actively supports and trains women in the IT and Tech industry, directly contributing to the Kingdom's goal of increasing female workforce participation.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-yellow-400 text-xl">
                    <i className="fa-solid fa-handshake"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white">Localization & Training</h4>
                    <p className="text-slate-400 text-sm mt-1">
                      We don't just deliver projects; we deliver capability. We partner with local teams to ensure knowledge transfer and long-term sustainability of digital assets.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:w-1/2 relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 text-center">
                  <div className="text-3xl font-bold text-yellow-400 mb-2">96%</div>
                  <div className="text-xs text-slate-400 uppercase">Digital Adoption</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 text-center mt-8">
                  <div className="text-3xl font-bold text-yellow-400 mb-2">2030</div>
                  <div className="text-xs text-slate-400 uppercase">Vision Goal</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 text-center">
                  <div className="text-3xl font-bold text-yellow-400 mb-2">24/7</div>
                  <div className="text-xs text-slate-400 uppercase">Support</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 text-center mt-8">
                  <div className="text-3xl font-bold text-yellow-400 mb-2">Global</div>
                  <div className="text-xs text-slate-400 uppercase">Standards</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
